import React, { useState, useEffect } from 'react';
import {
  Input,
  Select,
  DatePicker,
  Spin,
  Row,
  Col,
  Space,
  Upload,
  InputNumber,
  Card,
  Radio,
  Divider,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { RefundRequest, RefundStatus } from '../../pages/RefundRequests';
import callApi, { uploadImageApiCall } from '../../Api/config';
import { toastSuccess, toastError } from '../../service/Toast';
import { getAdminEmail, getAdminName, getRole } from '../../service/CookieManager';
import { REFUND_METHOD_OPTIONS, Role, getRefundFeeTypeLabel } from '../../constants/AppConstant';
import { InvertFillButton } from '../InvertFillButton';
import { Copy, FileText } from 'lucide-react';
import Accordian from '../Accordian/Accordian';
import RefundTrackingHistory from '../RefundTrackingHistory';
import RefundOrderPaymentDetails, { OrderPayment } from './RefundOrderPaymentDetails';
import RefundPaymentReconciliation from './RefundPaymentReconciliation';
import './style.scss';

type EligibilityDecision = 'ELIGIBLE' | 'NOT_ELIGIBLE' | '';

const { TextArea } = Input;

/** Matches the backend upload limit for refund confirmation documents. */
const MAX_CONFIRMATION_FILE_BYTES = 10 * 1024 * 1024;

interface RefundRequestDetailsProps {
  refund: RefundRequest;
  onStatusChange?: (updatedRefund: RefundRequest) => void;
  loading?: boolean;
}

const RefundRequestDetails: React.FC<RefundRequestDetailsProps> = ({
  refund,
  onStatusChange,
  loading = false,
}) => {
  const [currentRefund, setCurrentRefund] = useState<RefundRequest>(refund);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [orderPayment, setOrderPayment] = useState<OrderPayment | null>(null);

  // Refund Eligibility Confirmation (internal only)
  const [eligibilityDecision, setEligibilityDecision] = useState<EligibilityDecision>('');
  const [eligibleAmount, setEligibleAmount] = useState<number | null>(null);
  const [eligibilityRemarks, setEligibilityRemarks] = useState('');
  // Application Charges only
  const [expectedDate, setExpectedDate] = useState<moment.Moment | null>(null);
  const [paymentVoucherId, setPaymentVoucherId] = useState('');

  // Approval / Rejection
  const [decision, setDecision] = useState<'APPROVED' | 'REJECTED' | ''>('');
  const [approvedBy, setApprovedBy] = useState('');
  const [approvedAt, setApprovedAt] = useState<moment.Moment | null>(moment());
  const [amountApproved, setAmountApproved] = useState<number | null>(null);
  const [approvalRemarks, setApprovalRemarks] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectedBy, setRejectedBy] = useState('');
  const [rejectedAt, setRejectedAt] = useState<moment.Moment | null>(moment());

  // Refunded
  const [refundDate, setRefundDate] = useState<moment.Moment | null>(moment());
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [refundMethod, setRefundMethod] = useState<string>('');
  const [refundedBy, setRefundedBy] = useState<string>('');
  const [refundRemarks, setRefundRemarks] = useState('');
  const [confirmationFile, setConfirmationFile] = useState<File | null>(null);

  const loggedInUserName = getAdminName() || getAdminEmail() || 'Unknown User';
  const adminRole = getRole();
  const canConfirmEligibility = adminRole === Role.SUPER_ADMIN || adminRole === Role.ADMIN_OPS;
  const canDecide = adminRole === Role.SUPER_ADMIN;
  const canMarkRefunded = adminRole === Role.SUPER_ADMIN || adminRole === Role.ACCOUNTANT;
  const canReconcile = adminRole === Role.ACCOUNTANT || adminRole === Role.SUPER_ADMIN;
  // Fee type the internal workflow runs on: CMS-selected, else the applicant's choice.
  const effectiveFeeType = currentRefund.eligibilityFeeType || currentRefund.refundType;
  const isApplicationCharges = effectiveFeeType === 'GOVT_AND_COURIER';
  // Eligibility form fields follow the fee type the applicant chose in the refund request.
  const isApplicationChargesChoice = currentRefund.refundType === 'GOVT_AND_COURIER';
  const requestedAmount = Number(currentRefund.amountPaid ?? (currentRefund as any).amount ?? 0);
  const currency = currentRefund.currency || 'USD';
  const propRefundId = refund.id || (refund as any).refundRequestId || '';

  useEffect(() => {
    const initialAmount = Number(
      refund.amountApproved ??
        refund.eligibleAmount ??
        refund.amountPaid ??
        (refund as any).amount ??
        0
    );

    setCurrentRefund(refund);
    setOrderPayment(null);
    setEligibilityDecision('');
    setEligibleAmount(Number(refund.amountPaid ?? (refund as any).amount ?? 0) || null);
    setEligibilityRemarks('');
    setExpectedDate(null);
    setPaymentVoucherId('');
    setDecision('');
    setApprovedBy(refund.approvedBy || loggedInUserName);
    setApprovedAt(refund.approvedAt ? moment(refund.approvedAt) : moment());
    setAmountApproved(
      refund.amountApproved != null ? Number(refund.amountApproved) : initialAmount || null
    );
    setApprovalRemarks(refund.approvalRemarks || '');
    setRejectReason(refund.rejectionReason || '');
    setRejectedBy(refund.rejectedBy || loggedInUserName);
    setRejectedAt(refund.rejectedAt ? moment(refund.rejectedAt) : moment());
    setRefundDate(moment());
    setRefundAmount(
      String(refund.amountApproved ?? refund.amountPaid ?? (refund as any).amount ?? '')
    );
    setRefundMethod('');
    setRefundedBy(loggedInUserName);
    setRefundRemarks('');
    setConfirmationFile(null);

    if (!propRefundId) return;

    let cancelled = false;
    const hydrateRefundDetails = async () => {
      try {
        const response: any = await callApi(`/v1/refund/details/${propRefundId}`, 'GET');
        if (cancelled) return;
        const payload = response?.data?.data || response?.data;
        setOrderPayment(payload?.orderPayment || null);
        const detailList = payload?.details || payload?.transactionDetails || [];
        const latest = Array.isArray(detailList) ? detailList[0] : null;
        if (!latest && !payload?.refund) return;

        // Approval starts from the amount confirmed eligible.
        if (payload?.refund?.amountApproved == null && payload?.refund?.eligibleAmount != null) {
          setAmountApproved(Number(payload.refund.eligibleAmount));
        }

        setCurrentRefund(prev => ({
          ...prev,
          ...(payload?.refund || {}),
          amountPaid: Number(payload?.refund?.amount ?? prev.amountPaid) || prev.amountPaid,
          refundAmount:
            payload?.refund?.refundAmount != null
              ? Number(payload.refund.refundAmount)
              : prev.refundAmount,
          refundDetails: latest
            ? {
                refundDate: latest.dateOfRefund,
                refundAmount: Number(latest.amountOfRefund),
                refundMethod: latest.methodOfRefund,
                refundedBy: latest.refundedBy,
                refundedAt: latest.createdAt,
                notes: latest.remarks || '',
                confirmationDocumentUrl: latest.confirmationDocumentUrl,
                confirmationDocumentName: latest.confirmationDocumentName,
              }
            : prev.refundDetails,
        }));
      } catch (err) {
        console.error('Failed to hydrate refund details', err);
      }
    };

    hydrateRefundDetails();
    return () => {
      cancelled = true;
    };
    // Fetch once per refund id/status — do not depend on derived amounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propRefundId, refund.status]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '--';
    return moment(dateString).format('DD/MM/YYYY');
  };

  const paymentMethodRaw = String(currentRefund.paymentMethod || '').toLowerCase();
  const isVenmo = paymentMethodRaw.includes('venmo');
  const isZelle = paymentMethodRaw.includes('zelle');
  const isStripe =
    paymentMethodRaw.includes('stripe') || Boolean(currentRefund.stripePaymentIntentId);
  const isCard =
    isStripe ||
    paymentMethodRaw.includes('card') ||
    paymentMethodRaw.includes('credit') ||
    paymentMethodRaw.includes('debit');

  const renderPaymentMethodDetails = () => {
    if (isVenmo) {
      const venmoValue =
        (currentRefund as any).venmoId ||
        (currentRefund as any).venmo ||
        (currentRefund as any).transactionId ||
        currentRefund.lastFourDigits;
      return (
        <Col xs={24} sm={12} lg={6}>
          <div className="detail-row">
            <div className="detail-label">Venmo ID</div>
            <div className="detail-value">{venmoValue || '--'}</div>
          </div>
        </Col>
      );
    }

    if (isZelle) {
      const zelleValue =
        (currentRefund as any).zelleId ||
        (currentRefund as any).zelle ||
        (currentRefund as any).transactionId ||
        currentRefund.lastFourDigits;
      return (
        <Col xs={24} sm={12} lg={6}>
          <div className="detail-row">
            <div className="detail-label">Zelle ID</div>
            <div className="detail-value">{zelleValue || '--'}</div>
          </div>
        </Col>
      );
    }

    return (
      <>
        <Col xs={24} sm={12} lg={6}>
          <div className="detail-row">
            <div className="detail-label">Card Last 4 Digits</div>
            <div className="detail-value">
              {currentRefund.lastFourDigits
                ? `**** **** **** ${currentRefund.lastFourDigits}`
                : '--'}
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="detail-row">
            <div className="detail-label">Card Holder</div>
            <div className="detail-value">{currentRefund.cardHolderName || '--'}</div>
          </div>
        </Col>
      </>
    );
  };

  const handleCopyOrderId = async () => {
    const orderId = currentRefund.orderId;
    if (!orderId) {
      toastError('No Order ID to copy');
      return;
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(String(orderId));
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = String(orderId);
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toastSuccess('Order ID copied to clipboard');
    } catch (err) {
      console.error('Failed to copy Order ID:', err);
      toastError('Failed to copy Order ID');
    }
  };

  const refundId = currentRefund.id || (currentRefund as any).refundRequestId;

  // "Amount paid" cap: Documitra Fee → what was paid for the order. For
  // Application Charges the voucher ID is typed manually, so the server checks
  // it against that voucher's received amount when it is this order's voucher.
  const orderAmountPaid = orderPayment
    ? Number(orderPayment.amountPaid) > 0
      ? Number(orderPayment.amountPaid)
      : Number(orderPayment.totalDocumitraFee || 0)
    : null;
  const eligibilityPaidLimit = isApplicationChargesChoice ? null : orderAmountPaid;
  const eligibleAmountMax =
    eligibilityPaidLimit != null
      ? Math.min(requestedAmount, eligibilityPaidLimit)
      : requestedAmount;
  // Approval: the order-payment cap is checked here; the voucher cap by the server.
  const approvalPaidLimit = isApplicationCharges ? null : orderAmountPaid;
  const approvedAmountMax =
    approvalPaidLimit != null ? Math.min(requestedAmount, approvalPaidLimit) : requestedAmount;

  const formatUsd = (value: number) => `$${value.toFixed(2)}`;
  const eligibilityAmountPaidHint = () =>
    eligibilityPaidLimit != null ? `Amount paid: ${formatUsd(eligibilityPaidLimit)}` : null;

  // Inline amount validation: keep what was typed and flag it, instead of
  // antd min/max silently replacing the value on blur.
  const getAmountError = (
    value: number | null | undefined,
    max: number,
    maxLabel: string
  ): string | null => {
    if (value == null || Number.isNaN(Number(value))) return null;
    if (Number(value) <= 0) return 'Amount must be greater than 0';
    if (Number(value) > max) return `Cannot exceed ${maxLabel} (${formatUsd(max)})`;
    return null;
  };
  const limitLabel = (paidLimit: number | null) =>
    paidLimit != null && paidLimit < requestedAmount ? 'amount paid' : 'requested amount';
  const eligibleAmountError = getAmountError(
    eligibleAmount,
    eligibleAmountMax,
    limitLabel(eligibilityPaidLimit)
  );
  const approvedAmountError = getAmountError(
    amountApproved,
    approvedAmountMax,
    limitLabel(approvalPaidLimit)
  );
  const refundAmountMax = Number(currentRefund.amountApproved ?? requestedAmount);
  const refundAmountError = refundAmount
    ? getAmountError(parseFloat(refundAmount), refundAmountMax, 'approved amount')
    : null;

  const handleConfirmEligibility = async () => {
    if (!eligibilityDecision) {
      toastError('Please select Eligible or Not Eligible');
      return;
    }
    const isEligible = eligibilityDecision === 'ELIGIBLE';
    const amountName = isApplicationChargesChoice ? 'expected amount' : 'eligible amount';
    if (isEligible && (eligibleAmount == null || eligibleAmount <= 0)) {
      toastError(`Please enter a valid ${amountName}`);
      return;
    }
    if (isEligible && Number(eligibleAmount) > requestedAmount) {
      toastError(
        `${isApplicationChargesChoice ? 'Expected' : 'Eligible'} amount cannot exceed requested amount`
      );
      return;
    }
    if (
      isEligible &&
      eligibilityPaidLimit != null &&
      Number(eligibleAmount) > eligibilityPaidLimit
    ) {
      toastError(
        `${isApplicationChargesChoice ? 'Expected' : 'Eligible'} amount cannot exceed the amount paid (${formatUsd(eligibilityPaidLimit)})`
      );
      return;
    }
    const needsApplicationChargeFields = isEligible && isApplicationChargesChoice;
    if (needsApplicationChargeFields && !expectedDate) {
      toastError('Please select the expected date');
      return;
    }
    if (needsApplicationChargeFields && !paymentVoucherId.trim()) {
      toastError('Please enter the Payment Voucher ID');
      return;
    }
    if (!eligibilityRemarks.trim()) {
      toastError(isEligible ? 'Please enter the reason for refund' : 'Please enter remarks');
      return;
    }

    try {
      setUpdateLoading(true);
      const eligibilityAt = moment().toISOString();
      const response: any = await callApi(`/v1/refund/status/${refundId}/eligibility`, 'PUT', {
        eligibilityStatus: eligibilityDecision,
        eligibleAmount: isEligible ? eligibleAmount : undefined,
        eligibilityAt,
        eligibilityBy: loggedInUserName,
        remarks: eligibilityRemarks.trim(),
        expectedDate: needsApplicationChargeFields ? expectedDate?.format('YYYY-MM-DD') : undefined,
        paymentVoucherId: needsApplicationChargeFields ? paymentVoucherId.trim() : undefined,
      });

      if (response?.data?.isSuccess) {
        toastSuccess('Refund eligibility saved');
        const saved = response.data.data || {};
        const updatedRefund: RefundRequest = {
          ...currentRefund,
          eligibilityStatus: saved.eligibilityStatus ?? eligibilityDecision,
          eligibleAmount:
            saved.eligibleAmount != null
              ? Number(saved.eligibleAmount)
              : isEligible
                ? eligibleAmount
                : null,
          eligibilityBy: saved.eligibilityBy || loggedInUserName,
          eligibilityAt: saved.eligibilityAt || eligibilityAt,
          eligibilityRemarks: saved.eligibilityRemarks || eligibilityRemarks.trim(),
          eligibilityFeeType: saved.eligibilityFeeType || currentRefund.refundType,
          eligibilityExpectedDate:
            saved.eligibilityExpectedDate ??
            (needsApplicationChargeFields ? expectedDate?.format('YYYY-MM-DD') : null),
          eligibilityPaymentVoucherId:
            saved.eligibilityPaymentVoucherId ??
            (needsApplicationChargeFields ? paymentVoucherId.trim() : null),
        };
        setCurrentRefund(updatedRefund);
        if (isEligible) {
          setAmountApproved(Number(updatedRefund.eligibleAmount));
        } else {
          setDecision('REJECTED');
        }
        onStatusChange?.(updatedRefund);
      } else {
        toastError(response?.data?.message || 'Failed to save refund eligibility');
      }
    } catch (error) {
      console.error('Error saving refund eligibility:', error);
      toastError('An error occurred while saving refund eligibility');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleApproveOrReject = async () => {
    if (!decision) {
      toastError('Please select Approved or Rejected');
      return;
    }
    // Eligibility is optional for Super Admin only; other roles must confirm it first.
    if (!currentRefund.eligibilityStatus && !canDecide) {
      toastError('Please confirm refund eligibility first');
      return;
    }

    try {
      setUpdateLoading(true);

      if (decision === 'APPROVED') {
        if (amountApproved == null || amountApproved <= 0) {
          toastError('Please enter a valid amount approved');
          return;
        }
        if (amountApproved > requestedAmount) {
          toastError('Amount approved cannot exceed requested amount');
          return;
        }
        if (approvalPaidLimit != null && amountApproved > approvalPaidLimit) {
          toastError(
            `Amount approved cannot exceed the amount paid (${formatUsd(approvalPaidLimit)})`
          );
          return;
        }

        const response = await callApi(`/v1/refund/${refundId}`, 'PATCH', {
          status: 'APPROVED',
          approvedBy: approvedBy || loggedInUserName,
          approvedAt: (approvedAt || moment()).toISOString(),
          amountApproved,
          approvalRemarks,
          remarks: approvalRemarks,
        });

        if (response?.data?.success || response?.data?.isSuccess) {
          toastSuccess('Refund request approved successfully');
          const updatedRefund = {
            ...currentRefund,
            status: 'APPROVED' as RefundStatus,
            approvedBy: approvedBy || loggedInUserName,
            approvedAt: (approvedAt || moment()).toISOString(),
            amountApproved,
            approvalRemarks,
          };
          setCurrentRefund(updatedRefund);
          onStatusChange?.(updatedRefund);
        } else {
          toastError(response?.data?.message || 'Failed to approve refund');
        }
      } else {
        if (!rejectReason) {
          toastError('Please enter rejection reason');
          return;
        }

        const response = await callApi(`/v1/refund/${refundId}`, 'PATCH', {
          status: 'REJECTED',
          rejectedBy: rejectedBy || loggedInUserName,
          rejectedAt: (rejectedAt || moment()).toISOString(),
          rejectionReason: rejectReason,
        });

        if (response?.data?.success || response?.data?.isSuccess) {
          toastSuccess('Refund request rejected successfully');
          const updatedRefund = {
            ...currentRefund,
            status: 'REJECTED' as RefundStatus,
            rejectedBy: rejectedBy || loggedInUserName,
            rejectedAt: (rejectedAt || moment()).toISOString(),
            rejectionReason: rejectReason,
          };
          setCurrentRefund(updatedRefund);
          onStatusChange?.(updatedRefund);
        } else {
          toastError(response?.data?.message || 'Failed to reject refund');
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toastError('An error occurred while updating the status');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleMarkRefunded = async () => {
    if (!refundDate || !refundAmount || !refundMethod || !refundedBy) {
      toastError('Please fill in all refund details');
      return;
    }

    const maxRefundAmount = Number(currentRefund.amountApproved ?? requestedAmount);
    if (!(parseFloat(refundAmount) > 0)) {
      toastError('Refund amount must be greater than 0');
      return;
    }
    if (parseFloat(refundAmount) > maxRefundAmount) {
      toastError(
        `Refund amount cannot exceed the approved amount of $${maxRefundAmount.toFixed(2)}`
      );
      return;
    }

    // Confirmation document is optional and may be any file type (server limit 10 MB).
    if (confirmationFile && confirmationFile.size > MAX_CONFIRMATION_FILE_BYTES) {
      toastError('Confirmation document must be 10 MB or smaller');
      return;
    }

    try {
      setUpdateLoading(true);
      const formData = new FormData();
      formData.append('refundDate', refundDate.format('YYYY-MM-DD'));
      formData.append('refundAmount', String(parseFloat(refundAmount)));
      formData.append('refundMethod', refundMethod);
      formData.append('refundedBy', refundedBy);
      formData.append('remarks', refundRemarks || '');
      if (confirmationFile) {
        formData.append('confirmationDocument', confirmationFile);
      }

      const response: any = await uploadImageApiCall(
        `/v1/refund/status/${refundId}/refund`,
        formData,
        'PUT'
      );

      if (response?.isSuccess === 1 || response?.data?.success || response?.data?.isSuccess) {
        toastSuccess('Refund marked as processed successfully');
        const updatedRefund = {
          ...currentRefund,
          status: 'REFUNDED' as RefundStatus,
          refundAmount: parseFloat(refundAmount),
          refundDetails: {
            refundDate: refundDate.format('YYYY-MM-DD'),
            refundAmount: parseFloat(refundAmount),
            refundMethod,
            refundedBy,
            refundedAt: new Date().toISOString(),
            notes: refundRemarks,
            confirmationDocumentName: confirmationFile?.name,
            confirmationDocumentUrl: confirmationFile
              ? URL.createObjectURL(confirmationFile)
              : undefined,
          },
        };
        setCurrentRefund(updatedRefund);
        onStatusChange?.(updatedRefund);

        // Re-hydrate so confirmation URL is the persisted S3 link
        try {
          const detailsRes: any = await callApi(`/v1/refund/details/${refundId}`, 'GET');
          const payload = detailsRes?.data?.data || detailsRes?.data;
          const latest = payload?.details?.[0] || payload?.transactionDetails?.[0];
          if (latest?.confirmationDocumentUrl) {
            setCurrentRefund(prev => ({
              ...prev,
              refundDetails: {
                ...prev.refundDetails!,
                confirmationDocumentUrl: latest.confirmationDocumentUrl,
                confirmationDocumentName: latest.confirmationDocumentName || confirmationFile?.name,
              },
            }));
          }
        } catch {
          // keep local object URL fallback
        }
      } else {
        toastError(response?.message || response?.data?.message || 'Failed to process refund');
      }
    } catch (error) {
      console.error('Error processing refund:', error);
      toastError('An error occurred while processing the refund');
    } finally {
      setUpdateLoading(false);
    }
  };

  const isPending = currentRefund.status === 'PENDING';
  const showApprovalReadOnly =
    currentRefund.status === 'APPROVED' ||
    currentRefund.status === 'REJECTED' ||
    currentRefund.status === 'REFUNDED';
  const showRefundedSection =
    currentRefund.status === 'APPROVED' || currentRefund.status === 'REFUNDED';

  const cardTitleStyle: React.CSSProperties = {
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: '#004195',
  };

  const hasEligibility = Boolean(currentRefund.eligibilityStatus);
  const isEligibleConfirmed = currentRefund.eligibilityStatus === 'ELIGIBLE';
  const showEligibilitySection = isPending || hasEligibility;
  // Super Admin may approve / reject without the (optional) eligibility step.
  const canActOnApproval = canDecide;

  const remarksBoxStyle: React.CSSProperties = {
    padding: 8,
    borderRadius: 4,
    backgroundColor: '#F4F8FD',
    border: '1px solid #d6e4f5',
  };

  // Field labels per fee type (Documitra Fee vs Application Charges).
  const eligibilityByLabel = (eligible: boolean, appCharges: boolean) => {
    if (!eligible) return 'Not Eligible By';
    return appCharges ? 'Created By' : 'Eligible By';
  };
  const eligibilityDateLabel = (eligible: boolean, appCharges: boolean) =>
    eligible && !appCharges ? 'Eligible Date' : 'Date';

  const feeTypeLabel = getRefundFeeTypeLabel(effectiveFeeType);
  const requestedFeeTypeLabel = getRefundFeeTypeLabel(currentRefund.refundType);

  // Criteria order: Eligible → Date before By; Not Eligible → By before Date.
  const orderByAndDate = (eligible: boolean, byField: JSX.Element, dateField: JSX.Element) =>
    eligible ? [dateField, byField] : [byField, dateField];

  const renderEligibilitySection = () => {
    if (hasEligibility) {
      const byField = (
        <Col xs={24} sm={12} lg={6} key="by">
          <div className="detail-row">
            <div className="detail-label">
              {eligibilityByLabel(isEligibleConfirmed, isApplicationCharges)}
            </div>
            <div className="detail-value">{currentRefund.eligibilityBy || '--'}</div>
          </div>
        </Col>
      );
      const dateField = (
        <Col xs={24} sm={12} lg={6} key="date">
          <div className="detail-row">
            <div className="detail-label">
              {eligibilityDateLabel(isEligibleConfirmed, isApplicationCharges)}
            </div>
            <div className="detail-value">
              {formatDate(currentRefund.eligibilityAt || undefined)}
            </div>
          </div>
        </Col>
      );

      return (
        <Row gutter={[48, 24]}>
          <Col xs={24} sm={12} lg={6}>
            <div className="detail-row">
              <div className="detail-label">Fee Type</div>
              <div className="detail-value">{feeTypeLabel}</div>
              {effectiveFeeType !== currentRefund.refundType && (
                <div className="fee-type-note">Customer requested: {requestedFeeTypeLabel}</div>
              )}
            </div>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <div className="detail-row">
              <div className="detail-label">Status</div>
              <span
                className={`status-badge ${
                  isEligibleConfirmed ? 'status-badge--approved' : 'status-badge--rejected'
                }`}
              >
                {isEligibleConfirmed ? 'Eligible' : 'Not Eligible'}
              </span>
            </div>
          </Col>
          {isEligibleConfirmed && (
            <Col xs={24} sm={12} lg={6}>
              <div className="detail-row">
                <div className="detail-label">
                  {isApplicationCharges ? 'Expected Amount' : 'Eligible Amount'}
                </div>
                <div className="detail-value amount-approved">
                  ${Number(currentRefund.eligibleAmount ?? 0).toFixed(2)} {currency}
                </div>
              </div>
            </Col>
          )}
          {isEligibleConfirmed && isApplicationCharges && (
            <>
              <Col xs={24} sm={12} lg={6}>
                <div className="detail-row">
                  <div className="detail-label">Expected Date</div>
                  <div className="detail-value">
                    {formatDate(currentRefund.eligibilityExpectedDate || undefined)}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <div className="detail-row">
                  <div className="detail-label">Payment Voucher ID</div>
                  <div className="detail-value">
                    {currentRefund.eligibilityPaymentVoucherId || '--'}
                  </div>
                </div>
              </Col>
            </>
          )}
          {orderByAndDate(isEligibleConfirmed, byField, dateField)}
          <Col xs={24}>
            <div className="detail-row">
              <div className="detail-label">
                {isEligibleConfirmed ? 'Remark (Refund Reason)' : 'Remarks'}
              </div>
              <div className="detail-value" style={remarksBoxStyle}>
                {currentRefund.eligibilityRemarks || '--'}
              </div>
            </div>
          </Col>
        </Row>
      );
    }

    if (!isPending) return null;

    if (!canConfirmEligibility) {
      return (
        <div className="awaiting-step-note">
          Awaiting eligibility confirmation by Ops Manager / Super Admin.
        </div>
      );
    }

    const isEligibleChoice = eligibilityDecision === 'ELIGIBLE';
    const byInput = (
      <Col xs={24} sm={12} lg={6} key="by">
        <div className="status-field">
          <div className="required-field">
            {eligibilityByLabel(isEligibleChoice, isApplicationChargesChoice)}
          </div>
          <Input className="content-rounded-field" value={loggedInUserName} disabled />
        </div>
      </Col>
    );
    const dateInput = (
      <Col xs={24} sm={12} lg={6} key="date">
        <div className="status-field">
          <div className="required-field">
            {eligibilityDateLabel(isEligibleChoice, isApplicationChargesChoice)}
          </div>
          <DatePicker
            className="content-rounded-field"
            style={{ width: '100%' }}
            value={moment()}
            disabled
            format="YYYY-MM-DD"
          />
        </div>
      </Col>
    );

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <div className="status-field">
            <div>Fee Type</div>
            <div className="detail-value">{requestedFeeTypeLabel}</div>
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="status-field">
            <div className={canDecide ? undefined : 'required-field'}>
              Eligibility{canDecide ? ' (Optional)' : ''}
            </div>
            <Radio.Group
              value={eligibilityDecision || undefined}
              onChange={event => setEligibilityDecision(event.target.value)}
              options={[
                { value: 'ELIGIBLE', label: 'Eligible' },
                { value: 'NOT_ELIGIBLE', label: 'Not Eligible' },
              ]}
            />
          </div>
        </Col>

        {eligibilityDecision && (
          <>
            {isEligibleChoice && (
              <Col xs={24} sm={12} lg={6}>
                <div className="status-field">
                  <div className="required-field">
                    {isApplicationChargesChoice ? 'Expected Amount (USD)' : 'Eligible Amount (USD)'}
                  </div>
                  <InputNumber
                    className="content-rounded-field"
                    style={{ width: '100%' }}
                    status={eligibleAmountError ? 'error' : undefined}
                    precision={2}
                    value={eligibleAmount ?? undefined}
                    onChange={value => setEligibleAmount(value)}
                  />
                  {eligibleAmountError ? (
                    <div className="amount-error">{eligibleAmountError}</div>
                  ) : (
                    eligibilityAmountPaidHint() && (
                      <div className="amount-paid-hint">{eligibilityAmountPaidHint()}</div>
                    )
                  )}
                </div>
              </Col>
            )}
            {isEligibleChoice && isApplicationChargesChoice && (
              <>
                <Col xs={24} sm={12} lg={6}>
                  <div className="status-field">
                    <div className="required-field">Expected Date</div>
                    <DatePicker
                      className="content-rounded-field"
                      style={{ width: '100%' }}
                      value={expectedDate}
                      onChange={value => setExpectedDate(value)}
                      format="YYYY-MM-DD"
                    />
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <div className="status-field">
                    <div className="required-field">Payment Voucher ID</div>
                    <Input
                      className="content-rounded-field"
                      value={paymentVoucherId}
                      onChange={event => setPaymentVoucherId(event.target.value)}
                      placeholder="e.g. PV-10001"
                    />
                  </div>
                </Col>
              </>
            )}
            {orderByAndDate(isEligibleChoice, byInput, dateInput)}
            <Col xs={24}>
              <div className="status-field">
                <div className="required-field">
                  {isEligibleChoice ? 'Remark (Refund Reason)' : 'Remarks'}
                </div>
                <TextArea
                  style={remarksBoxStyle}
                  className="content-rounded-field"
                  value={eligibilityRemarks}
                  onChange={e => setEligibilityRemarks(e.target.value)}
                  rows={3}
                  placeholder={
                    isEligibleChoice ? 'Reason for refund' : 'Reason the refund is not eligible'
                  }
                />
              </div>
            </Col>
          </>
        )}

        <Col span={24}>
          <InvertFillButton
            variant={eligibilityDecision === 'NOT_ELIGIBLE' ? 'red' : 'blue'}
            onClick={handleConfirmEligibility}
            disabled={!eligibilityDecision}
          >
            Confirm Eligibility
          </InvertFillButton>
        </Col>
      </Row>
    );
  };

  return (
    <div>
      <Spin spinning={loading || updateLoading}>
        <div
          style={{
            padding: '16px',
            borderRadius: 8,
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            marginBottom: 16,
          }}
          className="details-content"
        >
          <RefundTrackingHistory
            refund={currentRefund}
            currency={currency}
            requestedAmount={requestedAmount}
          />
        </div>
        <div
          className="details-content"
          style={{
            borderRadius: 8,
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
          }}
        >
          <Accordian title="Refund Request Details" defaultOpen={false}>
            <Card
              title={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span style={cardTitleStyle}>Refund Request Details</span>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '3px 4px 3px 10px',
                        borderRadius: 20,
                        background: '#eef2ff',
                        border: '1px solid #c7d2fe',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: '#3730a3',
                        }}
                      ></span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#1e1b4b',
                        }}
                      >
                        {currentRefund.orderId || '--'}
                      </span>
                      <button
                        type="button"
                        onClick={handleCopyOrderId}
                        title="Copy Order ID"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 22,
                          height: 22,
                          padding: 0,
                          margin: 0,
                          borderRadius: '50%',
                          border: 'none',
                          background: '#4f46e5',
                          color: '#ffffff',
                          cursor: 'pointer',
                          transition: 'background 0.15s ease',
                        }}
                        onMouseEnter={e =>
                          ((e.currentTarget as HTMLButtonElement).style.background = '#4338ca')
                        }
                        onMouseLeave={e =>
                          ((e.currentTarget as HTMLButtonElement).style.background = '#4f46e5')
                        }
                      >
                        <Copy size={11} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#075dc5',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: '#53657f',
                          fontWeight: 600,
                        }}
                      >
                        Request Date:
                      </span>
                      <span>{formatDate(currentRefund.createdAt)}</span>
                    </div>

                    {/* Divider */}
                    <span
                      style={{
                        display: 'block',
                        width: '1px',
                        height: '24px',
                        backgroundColor: '#c7d2fe',
                        flexShrink: 0,
                      }}
                    />

                    <div
                      style={{
                        padding: '4px 10px',
                        borderRadius: 4,
                        background:
                          currentRefund.status === 'PENDING'
                            ? '#fef3c7'
                            : currentRefund.status === 'APPROVED'
                              ? '#d1fae5'
                              : currentRefund.status === 'REFUNDED'
                                ? '#dbeafe'
                                : '#fee2e2',
                        color:
                          currentRefund.status === 'PENDING'
                            ? '#d97706'
                            : currentRefund.status === 'APPROVED'
                              ? '#15803d'
                              : currentRefund.status === 'REFUNDED'
                                ? '#1d4ed8'
                                : '#b91c1c',
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {currentRefund.status}
                    </div>
                  </div>
                </div>
              }
              style={{ marginBottom: 16, borderRadius: 8 }}
              bordered
            >
              {/* Applicant */}
              <Row gutter={[48, 24]}>
                <Col xs={24} sm={12} lg={6}>
                  <div className="detail-row">
                    <div className="detail-label">Name</div>
                    <div className="detail-value">
                      {currentRefund.name || (currentRefund as any).applicantName || '--'}
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <div className="detail-row">
                    <div className="detail-label">Email</div>
                    <div className="detail-value">{currentRefund.email || '--'}</div>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <div className="detail-row">
                    <div className="detail-label">Phone</div>
                    <div className="detail-value">{currentRefund.mobileNumber || '--'}</div>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <div className="detail-row">
                    <div className="detail-label">Fee Type</div>
                    <div className="detail-value">
                      {getRefundFeeTypeLabel(currentRefund.refundType)}
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <div className="detail-row">
                    <div className="detail-label">Amount Requested</div>
                    <div className="detail-value amount-requested">
                      ${requestedAmount.toFixed(2)} {currency}
                    </div>
                  </div>
                </Col>
                {/* <Col xs={24} sm={12} lg={6}>
                <div className="detail-row">
                  <div className="detail-label">Order Id</div>
                  <div className="detail-value">{currentRefund.orderId || '--'}</div>
                </div>
              </Col> */}
                {/* <Col xs={24} sm={12} lg={6}>
                <div className="detail-row">
                  <div className="detail-label">Payment Date</div>
                  <div className="detail-value">{formatDate(currentRefund.paymentDate)}</div>
                </div>
              </Col> */}
                <Col xs={24} sm={12} lg={6}>
                  <div className="detail-row">
                    <div className="detail-label">Payment Method</div>
                    <div className="detail-value">{currentRefund.paymentMethod || '--'}</div>
                  </div>
                </Col>

                {renderPaymentMethodDetails()}

                <Col xs={24} sm={12} lg={6}>
                  <div className="detail-row">
                    <div className="detail-label">Service Name</div>
                    <div className="detail-value">{currentRefund.applicationType || '--'}</div>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <div className="detail-row">
                    <div className="detail-label">Payment Date</div>
                    <div className="detail-value">{formatDate(currentRefund.paymentDate)}</div>
                  </div>
                </Col>
                <Col xs={24}>
                  <div className="detail-row">
                    <div className="detail-label">Reason for Refund</div>
                    <div
                      className="detail-value"
                      style={{
                        padding: 8,
                        borderRadius: 4,
                        backgroundColor: '#F4F8FD',
                        border: '1px solid #d6e4f5',
                      }}
                    >
                      {currentRefund.refundReason || '--'}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            <RefundOrderPaymentDetails orderPayment={orderPayment} titleStyle={cardTitleStyle} />

            {/* Refund Eligibility Confirmation — internal team only, never shown to the user */}
            {showEligibilitySection && (
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={cardTitleStyle}>Refund Eligibility Confirmation</span>
                    <span className="internal-only-tag">Internal only</span>
                  </div>
                }
                style={{ marginBottom: 16, borderRadius: 8 }}
                bordered
              >
                {renderEligibilitySection()}
              </Card>
            )}

            {/* Accountant Payment Reconciliation — independent of approval; optional for Super Admin only */}
            {isApplicationCharges && (
              <RefundPaymentReconciliation
                refund={currentRefund}
                canReconcile={canReconcile}
                isSuperAdmin={canDecide}
                titleStyle={cardTitleStyle}
                onSaved={patch => {
                  const updatedRefund = { ...currentRefund, ...patch };
                  setCurrentRefund(updatedRefund);
                  onStatusChange?.(updatedRefund);
                }}
              />
            )}

            {/* Approval / Rejection */}
            {(isPending || showApprovalReadOnly) && (
              <Card
                title={<span style={cardTitleStyle}>Admin Team Approval</span>}
                style={{ marginBottom: 16, borderRadius: 8 }}
                bordered
              >
                {isPending && !canActOnApproval ? (
                  <div className="awaiting-step-note">
                    {hasEligibility
                      ? 'Awaiting approval / rejection by Super Admin.'
                      : 'Available after refund eligibility is confirmed.'}
                  </div>
                ) : isPending ? (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="status-field">
                        <div className="required-field">Decision</div>
                        <Radio.Group
                          value={decision || undefined}
                          onChange={event => setDecision(event.target.value)}
                          options={[
                            // Super Admin may approve even a refund marked Not Eligible.
                            { value: 'APPROVED', label: 'Approved' },
                            { value: 'REJECTED', label: 'Rejected' },
                          ]}
                        />
                      </div>
                    </Col>

                    {decision === 'APPROVED' && (
                      <>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="status-field">
                            <div className="required-field">Amount Approved (USD)</div>
                            <InputNumber
                              className="content-rounded-field"
                              style={{ width: '100%' }}
                              status={approvedAmountError ? 'error' : undefined}
                              precision={2}
                              value={amountApproved ?? undefined}
                              onChange={value => setAmountApproved(value)}
                            />
                            {approvedAmountError ? (
                              <div className="amount-error">{approvedAmountError}</div>
                            ) : (
                              approvalPaidLimit != null && (
                                <div className="amount-paid-hint">
                                  Amount paid: {formatUsd(approvalPaidLimit)}
                                </div>
                              )
                            )}
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="status-field">
                            <div className="required-field">Approved By</div>
                            <Input
                              className="content-rounded-field"
                              value={approvedBy}
                              disabled
                              placeholder="Approved by"
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="status-field">
                            <div className="required-field">Approval Date</div>
                            <DatePicker
                              className="content-rounded-field"
                              style={{ width: '100%' }}
                              value={approvedAt}
                              disabled
                              format="YYYY-MM-DD"
                            />
                          </div>
                        </Col>

                        <Col xs={24}>
                          <div className="status-field">
                            <div>Remarks</div>
                            <TextArea
                              style={{
                                padding: 8,
                                borderRadius: 4,
                                backgroundColor: '#F4F8FD',
                                border: '1px solid #d6e4f5',
                              }}
                              className="content-rounded-field"
                              value={approvalRemarks}
                              onChange={e => setApprovalRemarks(e.target.value)}
                              rows={3}
                              placeholder="Approval remarks"
                            />
                          </div>
                        </Col>
                      </>
                    )}

                    {decision === 'REJECTED' && (
                      <>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="status-field">
                            <div className="required-field">Rejected By</div>
                            <Input
                              className="content-rounded-field"
                              value={rejectedBy}
                              disabled
                              placeholder="Rejected by"
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="status-field">
                            <div className="required-field">Rejection Date</div>
                            <DatePicker
                              className="content-rounded-field"
                              style={{ width: '100%' }}
                              value={rejectedAt}
                              disabled
                              format="YYYY-MM-DD"
                            />
                          </div>
                        </Col>
                        <Col xs={24}>
                          <div className="status-field">
                            <div className="required-field">Remarks</div>
                            <TextArea
                              style={{
                                padding: 8,
                                borderRadius: 4,
                                backgroundColor: '#F4F8FD',
                                border: '1px solid #d6e4f5',
                              }}
                              className="content-rounded-field"
                              value={rejectReason}
                              onChange={e => setRejectReason(e.target.value)}
                              rows={3}
                              placeholder="Rejection reason"
                            />
                          </div>
                        </Col>
                      </>
                    )}

                    <Col span={24}>
                      <Space>
                        <InvertFillButton
                          variant={decision === 'APPROVED' ? 'blue' : 'red'}
                          onClick={handleApproveOrReject}
                          disabled={!decision}
                        >
                          {decision === 'APPROVED'
                            ? 'Approve Request'
                            : decision === 'REJECTED'
                              ? 'Reject Request'
                              : 'Submit Decision'}
                        </InvertFillButton>
                      </Space>
                    </Col>
                  </Row>
                ) : showApprovalReadOnly ? (
                  <Row gutter={[48, 24]}>
                    {currentRefund.status === 'REJECTED' ? (
                      <>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Status</div>
                            <span className="status-badge status-badge--rejected">Rejected</span>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Rejected By</div>
                            <div className="detail-value">{currentRefund.rejectedBy || '--'}</div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Date</div>
                            <div className="detail-value">
                              {formatDate(currentRefund.rejectedAt)}
                            </div>
                          </div>
                        </Col>
                        <Col xs={24}>
                          <div className="detail-row">
                            <div className="detail-label">Remarks</div>
                            <div
                              className="detail-value"
                              style={{
                                padding: 8,
                                borderRadius: 4,
                                backgroundColor: '#F4F8FD',
                                border: '1px solid #d6e4f5',
                              }}
                            >
                              {currentRefund.rejectionReason || '--'}
                            </div>
                          </div>
                        </Col>
                      </>
                    ) : (
                      <>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Status</div>
                            <span className="status-badge status-badge--approved">Approved</span>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Approved By</div>
                            <div className="detail-value">{currentRefund.approvedBy || '--'}</div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Approval Date</div>
                            <div className="detail-value">
                              {formatDate(currentRefund.approvedAt)}
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Amount Approved</div>
                            <div className="detail-value amount-approved">
                              ${Number(currentRefund.amountApproved ?? requestedAmount).toFixed(2)}{' '}
                              USD
                            </div>
                          </div>
                        </Col>
                        <Col xs={24}>
                          <div className="detail-row">
                            <div className="detail-label">Remarks</div>
                            <div
                              className="detail-value"
                              style={{
                                padding: 8,
                                borderRadius: 4,
                                backgroundColor: '#F4F8FD',
                                border: '1px solid #d6e4f5',
                              }}
                            >
                              {currentRefund.approvalRemarks || '--'}
                            </div>
                          </div>
                        </Col>
                      </>
                    )}
                  </Row>
                ) : null}
              </Card>
            )}

            {/* diff */}

            {showRefundedSection && (
              <Card
                title={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: 16,
                    }}
                  >
                    <span style={cardTitleStyle}>Refunded</span>
                    {currentRefund.status === 'REFUNDED' && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#075dc5',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: '#53657f',
                            fontWeight: 600,
                          }}
                        >
                          Refund Date:
                        </span>
                        <span>
                          {formatDate(
                            currentRefund.refundDetails?.refundDate ||
                              (currentRefund as any).refundedAt
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                }
                style={{ marginBottom: 16, borderRadius: 8 }}
                bordered
              >
                {currentRefund.status === 'REFUNDED' ? (
                  <Row gutter={[48, 24]}>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="detail-row">
                        <div className="detail-label">Refund Amount</div>
                        <div className="detail-value amount-requested">
                          $
                          {Number(
                            currentRefund.refundDetails?.refundAmount ??
                              currentRefund.refundAmount ??
                              0
                          ).toFixed(2)}{' '}
                          USD
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="detail-row">
                        <div className="detail-label">Refund Method</div>
                        <div className="detail-value">
                          {currentRefund.refundDetails?.refundMethod || '--'}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="detail-row">
                        <div className="detail-label">Refunded By</div>
                        <div className="detail-value">
                          {currentRefund.refundDetails?.refundedBy || '--'}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="detail-row">
                        <div className="detail-label">Confirmation Document</div>
                        <div className="detail-value">
                          {currentRefund.refundDetails?.confirmationDocumentUrl ||
                          currentRefund.refundDetails?.confirmationDocumentName ? (
                            <a
                              href={currentRefund.refundDetails?.confirmationDocumentUrl || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                color: '#075dc5',
                                fontWeight: 600,
                                textDecoration: 'none',
                              }}
                            >
                              <FileText size={14} />
                              {currentRefund.refundDetails?.confirmationDocumentName ||
                                'View Document'}
                            </a>
                          ) : (
                            '--'
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24}>
                      <div className="detail-row">
                        <div className="detail-label">Remarks</div>
                        <div
                          className="detail-value"
                          style={{
                            padding: 8,
                            borderRadius: 4,
                            backgroundColor: '#F4F8FD',
                            border: '1px solid #d6e4f5',
                          }}
                        >
                          {currentRefund.refundDetails?.notes || '--'}
                        </div>
                      </div>
                    </Col>
                  </Row>
                ) : isApplicationCharges && !currentRefund.reconciliationAt && !canDecide ? (
                  <div className="awaiting-step-note">
                    Available after Accountant payment reconciliation.
                  </div>
                ) : !canMarkRefunded ? (
                  <div className="awaiting-step-note">
                    Awaiting refund processing by Accountant / Super Admin.
                  </div>
                ) : (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="status-field">
                        <div className="required-field">Refund Method</div>
                        <Select
                          className="content-rounded-field"
                          style={{ width: '100%' }}
                          dropdownClassName="select-checkbox-dropdown"
                          value={refundMethod || undefined}
                          onChange={value => setRefundMethod(value)}
                          options={REFUND_METHOD_OPTIONS.map(opt => ({
                            value: opt.value,
                            label: opt.name,
                          }))}
                          placeholder="Select method"
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="status-field">
                        <div className="required-field">Refund Amount (USD)</div>
                        <Input
                          className="content-rounded-field"
                          value={refundAmount}
                          onChange={e => {
                            const value = e.target.value;
                            if (!value || /^\d*\.?\d*$/.test(value)) {
                              setRefundAmount(value);
                            }
                          }}
                          status={refundAmountError ? 'error' : undefined}
                          prefix="$"
                          placeholder="Refund amount"
                        />
                        {refundAmountError ? (
                          <div className="amount-error">{refundAmountError}</div>
                        ) : (
                          <div className="amount-paid-hint">
                            Approved amount: {formatUsd(refundAmountMax)}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="status-field">
                        <div className="required-field">Refund Date</div>
                        <DatePicker
                          className="content-rounded-field"
                          style={{ width: '100%' }}
                          value={refundDate}
                          format="YYYY-MM-DD"
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="status-field">
                        <div className="required-field">Refunded By</div>
                        <Input
                          className="content-rounded-field"
                          value={refundedBy}
                          disabled
                          placeholder="Refunded by"
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={12}>
                      <div className="status-field ">
                        <div className="">Confirmation (Optional)</div>
                        <Upload.Dragger
                          style={{
                            height: '80px',
                            padding: '10px 16px',
                            borderRadius: '8px',
                          }}
                          beforeUpload={file => {
                            setConfirmationFile(file);
                            return false;
                          }}
                          maxCount={1}
                          showUploadList={false}
                          onRemove={() => setConfirmationFile(null)}
                        >
                          <div className="confirmation-upload-content">
                            <UploadOutlined className="confirmation-upload-icon" />
                            {confirmationFile ? (
                              <>
                                <div
                                  className="confirmation-upload-file"
                                  style={{ fontSize: '12px' }}
                                >
                                  {confirmationFile.name}
                                </div>
                                <div
                                  className="confirmation-upload-hint"
                                  style={{ fontSize: '12px' }}
                                >
                                  Click to replace the document
                                </div>
                              </>
                            ) : (
                              <>
                                <div
                                  className="confirmation-upload-text"
                                  style={{ fontSize: '12px' }}
                                >
                                  Click or drag a file here
                                </div>
                                <div
                                  className="confirmation-upload-hint"
                                  style={{ fontSize: '12px' }}
                                >
                                  Upload confirmation document
                                </div>
                              </>
                            )}
                          </div>
                        </Upload.Dragger>
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={12}>
                      <div className="status-field">
                        <div>Remarks</div>
                        <TextArea
                          style={{
                            height: 80,
                            padding: 8,
                            borderRadius: 8,
                            backgroundColor: '#F4F8FD',
                            border: '1px solid #d6e4f5',
                          }}
                          className="content-rounded-field"
                          value={refundRemarks}
                          onChange={e => setRefundRemarks(e.target.value)}
                          placeholder="Refund remarks"
                        />
                      </div>
                    </Col>
                    <Col span={24}>
                      <InvertFillButton variant="blue" onClick={handleMarkRefunded}>
                        Mark as Refunded
                      </InvertFillButton>
                    </Col>
                  </Row>
                )}
              </Card>
            )}
          </Accordian>
        </div>
      </Spin>
    </div>
  );
};

export default RefundRequestDetails;
